import { useEffect, useMemo, useRef, useState, useSyncExternalStore, type RefObject } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { PageHeader } from '@/components/PageHeader';
import { Button, ButtonLink } from '@/components/ui/Button';
import { OverflowMenu } from '@/components/ui/OverflowMenu';
import { ErrorState, LoadingState, StateBlock } from '@/components/ui/StateBlock';
import { Surface } from '@/components/ui/Surface';
import { useCurrentUserQuery } from '@/features/user/queries';
import {
  formatDateTime,
  formatDurationMs,
  formatFileSize,
  formatStageIntervalRu,
} from '@/lib/format';
import { describeError, isNotFoundError } from '@/lib/http';

import type { ProcessingStageJob } from '../api';
import {
  useChatGptResultsQuery,
  useDiarizationMetaQuery,
  useDiarizedUtterancesInfiniteQuery,
  usePlayerUrlQuery,
  usePurgeTranscriptionMutation,
  useRetryDiarization,
  useRetryMediaProcessing,
  useRetryTranscription,
  useTranscriptionQuery,
  useTranscriptMetaQuery,
  useTranscriptSegmentsInfiniteQuery,
} from '../queries';
import {
  describeStatus,
  isPlayerReadyStatus,
  isTranscriptAvailableStatus,
  resolvePipelineViewStages,
  type PipelineViewStageState,
} from '../status';
import { DIARIZATION_ENABLED } from '../pipelineFeatures';
import { StatusBadge } from '../components/StatusBadge';
import { TranscriptExportActions } from '../components/TranscriptExportActions';
import { SpeakersSection } from '../components/SpeakersSection';
import {
  ChatGptResultsSection,
  type ChatGptCreateRequest,
  type ChatGptViewRequest,
} from '../components/ChatGptResultsSection';
import {
  ChatGptAnalysisWizard,
  type SelectedAnalysisPrompt,
} from '../components/ChatGptAnalysisWizard';
import { NextStepCard, resolveNextStepState } from '../components/NextStepCard';
import { EditMeetingModal } from '../components/EditMeetingModal';
import { groupTranscriptParagraphs } from '../groupTranscriptParagraphs';
import {
  formatClientLabel,
  formatMeetingDate,
  formatMeetingType,
} from '../meetingLabels';
import { createPlaybackTimeStore, type PlaybackTimeStore } from '../playbackTimeStore';

function Placeholder({ title, description }: { title: string; description: string }) {
  return (
    <div className="notice notice--muted">
      <div>
        <p className="notice__title">{title}</p>
        <p className="notice__text">{description}</p>
      </div>
    </div>
  );
}

/**
 * Вкладка резюме скрыта в MVP; код сохранён для следующего этапа.
 * Не подключать к UI, пока суммаризация не будет продуктово готова.
 */
function SummaryTab() {
  return (
    <Placeholder
      title="Резюме пока недоступно"
      description="Резюме будет доступно после подключения следующего этапа обработки."
    />
  );
}

void SummaryTab;

/**
 * Transcript view: shows diarized utterances when available, otherwise Whisper segments.
 */
function TranscriptResult({
  transcriptionId,
  status,
  playbackStore,
  onSeek,
  diarizationMeta,
}: {
  transcriptionId: string;
  status: string | null;
  playbackStore: PlaybackTimeStore;
  onSeek: (startMs: number) => void;
  diarizationMeta: ReturnType<typeof useDiarizationMetaQuery>['data'];
}) {
  const currentTimeMs = useSyncExternalStore(
    playbackStore.subscribe,
    playbackStore.getSnapshot,
    playbackStore.getSnapshot,
  );
  const statusLower = status?.trim().toLowerCase() ?? '';
  const showDiarizedView =
    DIARIZATION_ENABLED && (statusLower === 'diarized' || statusLower === 'ready');

  const segmentsQuery = useTranscriptSegmentsInfiniteQuery(
    transcriptionId,
    showDiarizedView ? null : status,
  );
  const utterancesQuery = useDiarizedUtterancesInfiniteQuery(
    transcriptionId,
    showDiarizedView ? status : null,
  );

  const activeQuery = showDiarizedView ? utterancesQuery : segmentsQuery;

  if (!isTranscriptAvailableStatus(status)) {
    return (
      <Placeholder
        title="Расшифровка пока недоступна"
        description="Текст появится здесь после того, как запись будет обработана сервисом распознавания."
      />
    );
  }

  if (activeQuery.isPending) {
    return <LoadingState title="Загружаем текст расшифровки" />;
  }

  if (activeQuery.isError) {
    return (
      <ErrorState
        description="Не удалось загрузить текст расшифровки."
        details={describeError(activeQuery.error)}
        onRetry={() => {
          void activeQuery.refetch();
        }}
      />
    );
  }

  if (showDiarizedView && utterancesQuery.data !== undefined) {
    const utterances = utterancesQuery.data.pages.flatMap((page) => page.items);

    if (utterances.length === 0) {
      return (
        <Placeholder
          title="Расшифровка пуста"
          description="Сервис распознавания не обнаружил речи в этой записи."
        />
      );
    }

    const speakerColors = new Map<string, string>();
    if (diarizationMeta !== null && diarizationMeta !== undefined) {
      const colors = [
        '#2563eb',
        '#dc2626',
        '#16a34a',
        '#ea580c',
        '#9333ea',
        '#0891b2',
        '#ca8a04',
        '#db2777',
      ];
      diarizationMeta.speakers.forEach((speaker, idx) => {
        speakerColors.set(speaker.id, colors[idx % colors.length]!);
      });
    }

    return (
      <div className="transcript-panel">
        <div className="transcript" role="list" aria-label="Текст расшифровки">
          <div className="transcript__header" aria-hidden="true">
            <div className="transcript__header-time">Время</div>
            <div className="transcript__header-text">Расшифровка</div>
          </div>
          {utterances.map((utterance) => {
            const active =
              currentTimeMs >= utterance.startMs && currentTimeMs < utterance.endMs;
            const speakerColor = utterance.speaker
              ? speakerColors.get(utterance.speaker.id)
              : null;

            return (
              <div
                className={`transcript__row${active ? ' transcript__row--active' : ''}`}
                key={utterance.id}
                role="listitem"
              >
                <div className="transcript__meta">
                  <button
                    type="button"
                    className="transcript__time"
                    onClick={() => onSeek(utterance.startMs)}
                    title="Перейти к этому моменту в записи"
                    aria-label={`Перейти к ${formatDurationMs(String(utterance.startMs))}`}
                  >
                    <span className="transcript__time-icon" aria-hidden="true">
                      ▶
                    </span>
                    {formatDurationMs(String(utterance.startMs))}
                  </button>
                </div>
                <div>
                  {utterance.speaker !== null ? (
                    <div className="transcript__speaker">
                      <span
                        className="transcript__speaker-dot"
                        style={{ backgroundColor: speakerColor ?? '#94a3b8' }}
                      />
                      <span>{utterance.speaker.displayName}</span>
                    </div>
                  ) : null}
                  <p className="transcript__text">{utterance.text}</p>
                </div>
              </div>
            );
          })}
        </div>

        {utterancesQuery.hasNextPage ? (
          <Button
            type="button"
            variant="secondary"
            disabled={utterancesQuery.isFetchingNextPage}
            onClick={() => {
              void utterancesQuery.fetchNextPage();
            }}
          >
            {utterancesQuery.isFetchingNextPage ? 'Загрузка…' : 'Показать ещё'}
          </Button>
        ) : null}
      </div>
    );
  }

  const segments = segmentsQuery.data?.pages.flatMap((page) => page.items) ?? [];

  if (segments.length === 0) {
    return (
      <Placeholder
        title="Расшифровка пуста"
        description="Сервис распознавания не обнаружил речи в этой записи."
      />
    );
  }

  const paragraphs = groupTranscriptParagraphs(segments);

  return (
    <div className="transcript-panel">
      <div className="transcript" role="list" aria-label="Текст расшифровки">
        <div className="transcript__header" aria-hidden="true">
          <div className="transcript__header-time">Время</div>
          <div className="transcript__header-text">Расшифровка</div>
        </div>
        {paragraphs.map((paragraph) => {
          const active = currentTimeMs >= paragraph.startMs && currentTimeMs < paragraph.endMs;
          return (
            <div
              className={`transcript__row${active ? ' transcript__row--active' : ''}`}
              key={paragraph.id}
              role="listitem"
            >
              <div className="transcript__meta">
                <button
                  type="button"
                  className="transcript__time"
                  onClick={() => onSeek(paragraph.startMs)}
                  title="Перейти к этому моменту в записи"
                  aria-label={`Перейти к ${formatDurationMs(String(paragraph.startMs))}`}
                >
                  <span className="transcript__time-icon" aria-hidden="true">
                    ▶
                  </span>
                  {formatDurationMs(String(paragraph.startMs))}
                </button>
              </div>
              <p className="transcript__text">{paragraph.text}</p>
            </div>
          );
        })}
      </div>

      {segmentsQuery.hasNextPage ? (
        <Button
          type="button"
          variant="secondary"
          disabled={segmentsQuery.isFetchingNextPage}
          onClick={() => {
            void segmentsQuery.fetchNextPage();
          }}
        >
          {segmentsQuery.isFetchingNextPage ? 'Загрузка…' : 'Показать ещё'}
        </Button>
      ) : null}
    </div>
  );
}

function stageMarker(state: PipelineViewStageState): string {
  if (state === 'done') {
    return '✓';
  }
  if (state === 'current') {
    return '●';
  }
  if (state === 'failed') {
    return '!';
  }
  return '○';
}

function stageSubtitle(
  state: PipelineViewStageState,
  duration: string | null,
): string | null {
  if (state === 'upcoming') {
    return 'Будет доступно позже';
  }
  if (state === 'pending') {
    return 'Ожидает запуска';
  }
  if (state === 'failed') {
    return duration !== null ? `Ошибка · ${duration}` : 'Ошибка';
  }
  if (state === 'current') {
    return duration !== null ? `Прошло: ${duration}` : 'Выполняется';
  }
  if (state === 'done') {
    return duration !== null ? `Время выполнения: ${duration}` : null;
  }
  return null;
}

function PrepStages({
  status,
  failureStage,
  jobs,
  liveClock,
}: {
  status: string | null;
  failureStage: string | null;
  jobs: readonly ProcessingStageJob[];
  /** Живой таймер только пока идёт обработка — локальный state, без ре-рендера всей страницы. */
  liveClock: boolean;
}) {
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (!liveClock) {
      return;
    }
    setNowMs(Date.now());
    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [liveClock, status]);

  const stages = resolvePipelineViewStages(status, failureStage);
  if (stages === null) {
    return null;
  }

  const jobByType = new Map(jobs.map((job) => [job.type, job]));

  return (
    <ol className="prep-stages" aria-label="Этапы обработки">
      {stages.map((stage) => {
        const job = jobByType.get(stage.jobType);
        const showDuration =
          stage.state === 'done' || stage.state === 'current' || stage.state === 'failed';
        const duration = showDuration
          ? formatStageIntervalRu(job?.startedAt, job?.finishedAt, nowMs)
          : null;
        const subtitle = stageSubtitle(stage.state, duration);
        return (
          <li key={stage.id} className={`prep-stages__item prep-stages__item--${stage.state}`}>
            <span className="prep-stages__marker" aria-hidden="true">
              {stageMarker(stage.state)}
            </span>
            <div className="prep-stages__copy">
              <span className="prep-stages__label">{stage.label}</span>
              {subtitle !== null ? <span className="prep-stages__time">{subtitle}</span> : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function isCompletedStatus(status: string | null): boolean {
  const normalized = status?.trim().toLowerCase() ?? '';
  if (normalized === 'diarized' || normalized === 'ready') {
    return true;
  }
  if (!DIARIZATION_ENABLED && (normalized === 'transcribed' || normalized === 'diarizing')) {
    return true;
  }
  return false;
}

function isActiveProcessingStatus(status: string | null): boolean {
  if (status === null) {
    return false;
  }
  const normalized = status.trim().toLowerCase();
  if (normalized === 'failed' || normalized === 'canceled' || isCompletedStatus(status)) {
    return false;
  }
  return (
    normalized === 'uploading' ||
    normalized === 'uploaded' ||
    normalized === 'preparing' ||
    normalized === 'prepared' ||
    normalized === 'transcribing' ||
    normalized === 'transcribed' ||
    normalized === 'diarizing' ||
    normalized === 'summarizing'
  );
}

function DetailMedia({
  transcriptionId,
  status,
  durationMs,
  fileSizeBytes,
  createdAt,
  updatedAt,
  completedAt,
  detectedLanguage,
  meetingType,
  meetingDate,
  clientName,
  axiumParticipants,
  clientParticipantNames,
  createdByName,
  showSpeakersDeferredNote,
  audioRef,
  onTimeUpdate,
}: {
  transcriptionId: string;
  status: string | null;
  durationMs: string | null;
  fileSizeBytes: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  completedAt: string | null;
  detectedLanguage: string | null;
  meetingType: string | null;
  meetingDate: string | null;
  clientName: string | null;
  axiumParticipants: ReadonlyArray<{ id: string; displayName: string }>;
  clientParticipantNames: readonly string[];
  createdByName: string | null;
  showSpeakersDeferredNote: boolean;
  audioRef: RefObject<HTMLAudioElement>;
  onTimeUpdate: (currentTimeMs: number) => void;
}) {
  const enabled = isPlayerReadyStatus(status);
  const query = usePlayerUrlQuery(transcriptionId, status);

  const meetingDateLabel = (() => {
    const value = formatMeetingDate(meetingDate);
    return value === '—' ? 'Не указано' : value;
  })();
  const meetingTypeLabel = formatMeetingType(meetingType);
  const clientLabel = formatClientLabel(meetingType, clientName);
  const axiumNames = axiumParticipants.map((item) => item.displayName);
  const clientNames = meetingType === 'INTERNAL' ? [] : [...clientParticipantNames];
  const uploaderLabel =
    createdByName !== null && createdByName.trim() !== '' ? createdByName : 'Не указано';

  return (
    <section className="detail-media" aria-label="Аудио и сведения о встрече">
      <div className="detail-media__player">
        {!enabled ? (
          <p className="audio-player__hint">Аудио станет доступно после подготовки файла.</p>
        ) : null}
        {enabled && query.isPending ? <LoadingState title="Получаем ссылку на аудио" /> : null}
        {enabled && query.isError ? (
          <ErrorState
            description="Не удалось получить ссылку на аудио."
            details={describeError(query.error)}
            onRetry={() => {
              void query.refetch();
            }}
          />
        ) : null}
        {enabled && query.data !== undefined ? (
          <audio
            ref={audioRef}
            className="audio-player"
            controls
            preload="metadata"
            src={query.data.url}
            onTimeUpdate={(event) => {
              onTimeUpdate(Math.floor(event.currentTarget.currentTime * 1000));
            }}
          >
            <track kind="captions" />
          </audio>
        ) : null}
      </div>

      <div className="detail-media__meeting">
        <h3 className="detail-media__meeting-title">О встрече</h3>
        <dl className="detail-media__meeting-grid">
          <div>
            <dt>Дата встречи</dt>
            <dd>{meetingDateLabel}</dd>
          </div>
          <div>
            <dt>Тип встречи</dt>
            <dd>{meetingTypeLabel === 'Не указан' ? 'Не указано' : meetingTypeLabel}</dd>
          </div>
          <div>
            <dt>Клиент</dt>
            <dd>{clientLabel === 'Не указаны' ? 'Не указано' : clientLabel}</dd>
          </div>
          <div>
            <dt>Участники AXIUM</dt>
            <dd>
              <PeopleList names={axiumNames} />
            </dd>
          </div>
          <div>
            <dt>Участники клиента</dt>
            <dd>
              <PeopleList names={clientNames} />
            </dd>
          </div>
          <div>
            <dt>Загрузил</dt>
            <dd>{uploaderLabel}</dd>
          </div>
        </dl>
      </div>

      <details className="detail-media__service">
        <summary className="detail-media__service-summary">
          <span>Служебные данные</span>
          <span className="detail-media__service-chevron" aria-hidden="true">
            ▾
          </span>
        </summary>
        <dl className="detail-media__service-grid">
          <div>
            <dt>Длительность</dt>
            <dd>{formatDurationMs(durationMs)}</dd>
          </div>
          <div>
            <dt>Размер файла</dt>
            <dd>{formatFileSize(fileSizeBytes)}</dd>
          </div>
          <div>
            <dt>Язык</dt>
            <dd>{detectedLanguage ?? 'Не указано'}</dd>
          </div>
          <div>
            <dt>Создано</dt>
            <dd>{formatDateTime(createdAt)}</dd>
          </div>
          <div>
            <dt>Обновлено</dt>
            <dd>{formatDateTime(updatedAt)}</dd>
          </div>
          <div>
            <dt>Завершено</dt>
            <dd>{formatDateTime(completedAt)}</dd>
          </div>
        </dl>
        {showSpeakersDeferredNote ? (
          <p className="detail-media__service-note">
            Определение говорящих будет доступно позже
          </p>
        ) : null}
      </details>
    </section>
  );
}

function PeopleList({ names }: { names: readonly string[] }) {
  if (names.length === 0) {
    return <>Не указано</>;
  }

  return (
    <ul className="meeting-people">
      {names.map((name, index) => (
        <li key={`${index}-${name}`} className="meeting-people__chip">
          {name}
        </li>
      ))}
    </ul>
  );
}

export function TranscriptionDetailPage() {
  const { id } = useParams<'id'>();
  const navigate = useNavigate();
  const currentUser = useCurrentUserQuery();
  const purgeMutation = usePurgeTranscriptionMutation();
  const query = useTranscriptionQuery(id);
  const retryMedia = useRetryMediaProcessing(id);
  const retryTranscript = useRetryTranscription(id);
  const retryDiarization = useRetryDiarization(id);
  const metaQuery = useTranscriptMetaQuery(id, query.data?.status);
  const diarizationQuery = useDiarizationMetaQuery(id, query.data?.status);
  const chatGptResultsQuery = useChatGptResultsQuery(
    id,
    isTranscriptAvailableStatus(query.data?.status ?? null),
  );
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const playbackStore = useMemo(() => createPlaybackTimeStore(), []);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [createRequest, setCreateRequest] = useState<ChatGptCreateRequest | null>(null);
  const [viewRequest, setViewRequest] = useState<ChatGptViewRequest | null>(null);
  const [requestNonce, setRequestNonce] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const isAdmin = currentUser.data?.role === 'ADMIN';

  const seekTo = (startMs: number) => {
    const audio = audioRef.current;
    if (audio !== null) {
      audio.currentTime = startMs / 1000;
    }
    playbackStore.setTimeMs(startMs);
  };

  if (query.isPending) {
    return (
      <Surface>
        <LoadingState title="Загружаем расшифровку" />
      </Surface>
    );
  }

  if (query.isError) {
    if (isNotFoundError(query.error)) {
      return (
        <Surface>
          <StateBlock
            icon="?"
            title="Расшифровка не найдена"
            description="Запись удалена или ссылка неверна."
            action={<ButtonLink to="/transcriptions">К списку расшифровок</ButtonLink>}
          />
        </Surface>
      );
    }

    return (
      <Surface>
        <ErrorState
          description="Не удалось получить данные записи. Повторите запрос или вернитесь к списку."
          details={describeError(query.error)}
          onRetry={() => {
            void query.refetch();
          }}
        />
      </Surface>
    );
  }

  const detail = query.data;
  const isCreator =
    currentUser.data?.id !== undefined &&
    currentUser.data.id !== null &&
    currentUser.data.id === detail.createdById;
  const canEditMeeting = isAdmin || isCreator;
  const failureStage = detail.failureStage?.trim().toUpperCase() ?? '';
  const isDiarizationFailure = failureStage === 'DIARIZATION';
  /** Пока диаризация выключена, ошибки/статусы этапа говорящих не показываем как сбой. */
  const suppressDiarizationUi = !DIARIZATION_ENABLED && isDiarizationFailure;
  const statusForUi = suppressDiarizationUi ? 'TRANSCRIBED' : detail.status;
  const statusLower = statusForUi?.trim().toLowerCase() ?? '';
  const showCompactProgress = isActiveProcessingStatus(statusForUi);
  const isTranscriptionFailure = failureStage === 'TRANSCRIPTION';
  const activeRetry = suppressDiarizationUi
    ? retryMedia
    : isDiarizationFailure
      ? retryDiarization
      : isTranscriptionFailure
        ? retryTranscript
        : retryMedia;
  const retryLabel = isDiarizationFailure
    ? 'Повторить определение говорящих'
    : isTranscriptionFailure
      ? 'Повторить распознавание'
      : 'Повторить подготовку';
  const detectedLanguage = metaQuery.data?.detectedLanguage ?? null;
  const jobs = detail.processingJobs ?? [];
  const progressPercent = detail.progress ?? 0;
  const transcriptReady = isTranscriptAvailableStatus(statusForUi);
  const chatGptItems = chatGptResultsQuery.data?.items ?? [];
  const latestResult = chatGptItems[0] ?? null;
  const nextStepState = resolveNextStepState({
    transcriptReady,
    hasChatGptResult: chatGptItems.length > 0,
  });
  const meetingExport = {
    title: detail.title ?? '',
    meetingDate: detail.meetingDate,
    meetingType: detail.meetingType,
    clientName: detail.clientName,
    axiumParticipantNames: detail.axiumParticipants.map((item) => item.displayName),
  };

  const openAttachForm = (prompt: SelectedAnalysisPrompt) => {
    const nonce = requestNonce + 1;
    setRequestNonce(nonce);
    setCreateRequest({ prompt, nonce });
  };

  const openLatestResult = () => {
    if (latestResult === null) {
      return;
    }
    const nonce = requestNonce + 1;
    setRequestNonce(nonce);
    setViewRequest({ resultId: latestResult.id, nonce });
  };

  const retryButton =
    !suppressDiarizationUi && (statusLower === 'failed' || isDiarizationFailure) ? (
      <Button
        type="button"
        variant="secondary"
        disabled={activeRetry.isPending}
        onClick={() => {
          if (isDiarizationFailure) {
            void retryDiarization.mutateAsync({}).catch(() => undefined);
          } else if (isTranscriptionFailure) {
            void retryTranscript.mutateAsync().catch(() => undefined);
          } else {
            void retryMedia.mutateAsync().catch(() => undefined);
          }
        }}
      >
        {activeRetry.isPending ? 'Повтор…' : retryLabel}
      </Button>
    ) : null;

  const headerMenuItems = isAdmin
    ? [
        {
          id: 'delete',
          label: purgeMutation.isPending ? 'Удаление…' : 'Удалить встречу',
          danger: true,
          disabled: purgeMutation.isPending,
          onSelect: () => {
            const title = detail.title ?? detail.id;
            const confirmed = window.confirm(
              `Удалить встречу «${title}» безвозвратно?\nФайлы и расшифровка будут удалены.`,
            );
            if (!confirmed) {
              return;
            }
            purgeMutation.mutate(detail.id, {
              onSuccess: () => {
                void navigate('/transcriptions');
              },
              onError: (error) => {
                window.alert(`Не удалось удалить: ${describeError(error)}`);
              },
            });
          },
        },
      ]
    : [];

  return (
    <div className="detail-page">
      <PageHeader
        compact
        breadcrumbs={[
          { label: 'Журнал встреч', to: '/transcriptions' },
          { label: detail.title ?? detail.id },
        ]}
        title={detail.title ?? `Запись ${detail.id}`}
        badge={<StatusBadge status={statusForUi} />}
        actions={
          <div className="page-header__actions">
            <ButtonLink variant="secondary" to="/transcriptions">
              К списку
            </ButtonLink>
            {canEditMeeting ? (
              <Button type="button" variant="secondary" onClick={() => setEditOpen(true)}>
                Редактировать
              </Button>
            ) : null}
            <OverflowMenu label="Действия встречи" items={headerMenuItems} />
          </div>
        }
      />

      {detail.errorMessage !== null && !suppressDiarizationUi ? (
        <div className="notice notice--danger notice--with-action" role="alert">
          <div className="notice__body">
            <p className="notice__title">
              {isDiarizationFailure
                ? 'Не удалось определить говорящих'
                : 'Обработка завершилась ошибкой'}
            </p>
            <p className="notice__text">{detail.errorMessage}</p>
          </div>
          {retryButton}
        </div>
      ) : null}

      {!suppressDiarizationUi && activeRetry.isError ? (
        <div className="notice notice--danger" role="alert">
          <div className="notice__body">
            <p className="notice__title">Не удалось запустить повтор</p>
            <p className="notice__text">{describeError(activeRetry.error)}</p>
          </div>
        </div>
      ) : null}

      {showCompactProgress ? (
        <div className="detail-progress" role="status" aria-live="polite">
          <div className="detail-progress__row">
            <span className="detail-progress__label">{describeStatus(statusForUi).label}</span>
            <span className="detail-progress__percent">{progressPercent}%</span>
          </div>
          <div className="upload-progress__track" aria-hidden="true">
            <div
              className="upload-progress__bar"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <PrepStages
            status={statusForUi}
            failureStage={suppressDiarizationUi ? null : detail.failureStage}
            jobs={jobs}
            liveClock={showCompactProgress}
          />
        </div>
      ) : null}

      {!showCompactProgress && isCompletedStatus(statusForUi) ? (
        <details className="detail-progress detail-progress--settled">
          <summary className="detail-progress__summary">
            <span className="detail-progress__label">Обработка завершена</span>
            <span className="detail-progress__summary-hint">Подробности</span>
          </summary>
          <PrepStages
            status={statusForUi}
            failureStage={null}
            jobs={jobs}
            liveClock={false}
          />
        </details>
      ) : null}

      {statusLower === 'failed' && !suppressDiarizationUi ? (
        <div className="detail-progress detail-progress--failed" role="status">
          <PrepStages
            status={detail.status}
            failureStage={detail.failureStage}
            jobs={jobs}
            liveClock={false}
          />
        </div>
      ) : null}

      <NextStepCard
        state={nextStepState}
        status={statusForUi}
        latestResult={latestResult}
        onPrepareAnalysis={() => setWizardOpen(true)}
        onOpenLatestResult={openLatestResult}
      />

      {id !== undefined ? (
        <DetailMedia
          transcriptionId={id}
          status={statusForUi}
          durationMs={detail.durationMs}
          fileSizeBytes={detail.fileSizeBytes}
          createdAt={detail.createdAt}
          updatedAt={detail.updatedAt}
          completedAt={detail.completedAt}
          detectedLanguage={detectedLanguage}
          meetingType={detail.meetingType}
          meetingDate={detail.meetingDate}
          clientName={detail.clientName}
          axiumParticipants={detail.axiumParticipants}
          clientParticipantNames={detail.clientParticipantNames}
          createdByName={detail.createdByName}
          showSpeakersDeferredNote={!DIARIZATION_ENABLED}
          audioRef={audioRef}
          onTimeUpdate={(ms) => playbackStore.setTimeMs(ms)}
        />
      ) : null}

      {id !== undefined && DIARIZATION_ENABLED && isTranscriptAvailableStatus(statusForUi) ? (
        <Surface flush>
          <SpeakersSection
            transcriptionId={id}
            detail={{
              ...detail,
              status: statusForUi,
              failureStage: suppressDiarizationUi ? null : detail.failureStage,
              errorMessage: suppressDiarizationUi ? null : detail.errorMessage,
            }}
            diarizationMeta={diarizationQuery.data ?? null}
            isPending={diarizationQuery.isPending}
            isError={diarizationQuery.isError}
            error={diarizationQuery.error}
            onRetry={async () => {
              await retryDiarization.mutateAsync({ force: true });
            }}
            onStartDiarization={async () => {
              await retryDiarization.mutateAsync({});
            }}
            audioRef={audioRef}
          />
        </Surface>
      ) : null}

      {id !== undefined ? (
        <Surface flush>
          <ChatGptResultsSection
            transcriptionId={id}
            enabled={isTranscriptAvailableStatus(detail.status)}
            currentUserId={currentUser.data?.id}
            isAdmin={isAdmin}
            createRequest={createRequest}
            onCreateRequestConsumed={() => setCreateRequest(null)}
            viewRequest={viewRequest}
            onViewRequestConsumed={() => setViewRequest(null)}
            onAddAnother={() => setWizardOpen(true)}
          />
        </Surface>
      ) : null}

      <Surface flush>
        <div className="transcript-section">
          <div className="transcript-section__header">
            <h2 className="transcript-section__title">Расшифровка</h2>
            {id !== undefined ? (
              <TranscriptExportActions
                transcriptionId={id}
                enabled={isTranscriptAvailableStatus(detail.status)}
                meeting={meetingExport}
              />
            ) : null}
          </div>
          <TranscriptResult
            transcriptionId={id ?? ''}
            status={detail.status}
            playbackStore={playbackStore}
            onSeek={seekTo}
            diarizationMeta={diarizationQuery.data ?? undefined}
          />
        </div>
      </Surface>

      {id !== undefined ? (
        <ChatGptAnalysisWizard
          open={wizardOpen}
          onClose={() => setWizardOpen(false)}
          transcriptionId={id}
          meeting={meetingExport}
          meetingType={detail.meetingType}
          onAttachResult={openAttachForm}
        />
      ) : null}

      <EditMeetingModal
        open={editOpen}
        meeting={detail}
        onClose={() => setEditOpen(false)}
      />
    </div>
  );
}
